/**
 * AudioRecorder – Word Add-in version
 * -------------------------------------------------
 * • Same streaming pipeline (4096-sample chunks, 16 kHz)
 * • High-quality linear-interpolation resample
 * • RMS-based normalization (-18 dBFS target, +12 dB max)
 * • Binary Float32 little-endian payload
 * • Volume monitoring (frequency-data → average)
 * • AudioWorklet with verified registration + fallback to ScriptProcessor
 * -------------------------------------------------
 */

export class AudioRecorder {
  constructor(webSocketManager, configManager) {
    this.webSocketManager = webSocketManager; // must have .send(data)
    this.configManager = configManager;

    this.audioStream = null;
    this.audioContext = null;
    this.source = null;
    this.workletNode = null;
    this.processor = null;

    this.isRecording = false;
    this.eventListeners = {};

    // ----- Target parameters (must match server) -----
    this.targetSampleRate = 16000;          // 16 kHz
    this.chunkSize = 4096;                  // ≈256 ms @16 kHz
    this.resampleRatio = 1;

    // ----- Normalization -----
    this.normalizeToRmsDb = -18;            // target RMS
    this.maxGainDb = 12;                    // max boost
    this.peakLimit = 0.999;                 // avoid clipping

    // ----- Runtime state -----
    this.pendingChunkSamples = new Float32Array(0);
    this.chunkCounter = 0;
    this.sentConfig = false;
    this.isMonitoringVolume = false;
    this.analyser = null;
    this.volumeDataArray = null;
  }

  // ──────────────────────────────────────────────────────
  // Event system
  // ──────────────────────────────────────────────────────
  on(event, callback) {
    if (!this.eventListeners[event]) this.eventListeners[event] = [];
    this.eventListeners[event].push(callback);
  }

  emit(event, data) {
    if (this.eventListeners[event]) {
      this.eventListeners[event].forEach(cb => cb(data));
    }
  }

  // ──────────────────────────────────────────────────────
  // Microphone permission
  // ──────────────────────────────────────────────────────
  async requestMicPermission() {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: false,
          noiseSuppression: false,
          autoGainControl: false,
          channelCount: 1,
        },
      });
      stream.getTracks().forEach(t => t.stop());
      return true;
    } catch (err) {
      let msg = 'Microphone access denied. Please allow the add-in to use the microphone.';
      if (err.name === 'NotFoundError') msg = 'No microphone detected.';
      this.emit('error', msg);
      return false;
    }
  }

  // ──────────────────────────────────────────────────────
  // START RECORDING
  // ──────────────────────────────────────────────────────
  async start() {
    if (!(await this.requestMicPermission())) return false;

    try {
      this.audioStream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: false,
          noiseSuppression: false,
          autoGainControl: false,
          channelCount: 1,
        },
      });

      this.audioContext = new (window.AudioContext || window.webkitAudioContext)();
      this.source = this.audioContext.createMediaStreamSource(this.audioStream);

      this.originalSampleRate = this.audioContext.sampleRate;
      this.resampleRatio = this.originalSampleRate / this.targetSampleRate;
      console.log(
        `Mic ${this.originalSampleRate} Hz → resample to ${this.targetSampleRate} Hz (ratio ${this.resampleRatio.toFixed(3)})`
      );

      // Send config once (server expects it before audio)
      this.sendConfig();
      this.setupVolumeAnalysis();

      // ---- Prefer AudioWorklet (zero-copy) ----
      const workletReady = await this._tryLoadWorklet();

      if (workletReady) {
        this.workletNode = new AudioWorkletNode(this.audioContext, 'recorder-worklet', {
          processorOptions: { channelCount: 1 },
        });

        this.workletNode.port.onmessage = ev => {
          if (ev.data.type === 'audio-data' && this.isRecording) {
            this.processAudioData(ev.data.audioData);
          }
        };

        this.source.connect(this.workletNode);
        console.log('AudioWorklet active');
      } else {
        console.warn('AudioWorklet unavailable, falling back to ScriptProcessor');
        this.setupScriptProcessor();
      }

      this.isRecording = true;
      this.emit('started');
      return true;
    } catch (e) {
      console.error('start() failed', e);
      this.emit('error', e.message);
      return false;
    }
  }

  // ──────────────────────────────────────────────────────
  // Load & VERIFY the AudioWorklet module before using it.
  //
  // The bug: addModule() resolves as soon as the script is
  // *fetched and parsed*, but registerProcessor() runs
  // asynchronously inside the AudioWorkletGlobalScope.
  // Constructing AudioWorkletNode immediately after addModule()
  // can therefore race and throw:
  //   "The node name 'recorder-worklet' is not defined in
  //    AudioWorkletGlobalScope"
  //
  // Fix: after addModule(), attempt to construct the node in a
  // retry loop (up to 10 × 20 ms = 200 ms). On success the node
  // is discarded and we return true so the caller creates the
  // real one. Falls back to ScriptProcessor on any failure.
  // ──────────────────────────────────────────────────────
  async _tryLoadWorklet() {
    let workletUrl = null;
    try {
      const workletBlob = new Blob([this.workletCode], { type: 'application/javascript' });
      workletUrl = URL.createObjectURL(workletBlob);

      await this.audioContext.audioWorklet.addModule(workletUrl);

      // Verify the processor is actually registered by trying to
      // construct the node. Retry up to 10 times with 20 ms gaps.
      const MAX_RETRIES = 10;
      const RETRY_DELAY_MS = 20;

      for (let attempt = 1; attempt <= MAX_RETRIES; attempt++) {
        try {
          // Probe construction – if it throws the scope isn't ready yet
          const probe = new AudioWorkletNode(this.audioContext, 'recorder-worklet', {
            processorOptions: { channelCount: 1 },
          });
          // Immediately disconnect the probe; we just needed the check
          probe.disconnect();
          console.log(`AudioWorklet registered (verified on attempt ${attempt})`);
          return true;
        } catch (e) {
          if (attempt === MAX_RETRIES) {
            console.warn(`AudioWorklet not registered after ${MAX_RETRIES} attempts:`, e.message);
            return false;
          }
          // Wait and retry
          await new Promise(resolve => setTimeout(resolve, RETRY_DELAY_MS));
        }
      }
    } catch (e) {
      console.warn('AudioWorklet load failed:', e.message);
      return false;
    } finally {
      if (workletUrl) URL.revokeObjectURL(workletUrl);
    }
    return false;
  }

  // ──────────────────────────────────────────────────────
  // STOP RECORDING
  // ──────────────────────────────────────────────────────
  async stop() {
    // Flush any remaining samples
    if (this.isRecording && this.pendingChunkSamples.length > 0) {
      this.sendAudioChunk(new Float32Array(this.pendingChunkSamples));
      this.pendingChunkSamples = new Float32Array(0);
    }

    this.isRecording = false;
    this.isMonitoringVolume = false;

    // Volume analyser
    if (this.analyser) this.analyser.disconnect();

    // Nodes
    if (this.workletNode) this.workletNode.disconnect();
    if (this.processor) this.processor.disconnect();
    if (this.source) this.source.disconnect();

    // Context & stream
    if (this.audioContext) await this.audioContext.close();
    if (this.audioStream) this.audioStream.getTracks().forEach(t => t.stop());

    this.chunkCounter = 0;
    this.sentConfig = false;
    this.emit('stopped');
  }

  // ──────────────────────────────────────────────────────
  // AudioWorklet source (inline – no external file needed)
  // ──────────────────────────────────────────────────────
  get workletCode() {
    return `
      class RecorderWorklet extends AudioWorkletProcessor {
        process(inputs) {
          const input = inputs[0];
          if (input && input[0]) {
            const copy = input[0].slice();
            this.port.postMessage(
              { type: 'audio-data', audioData: copy },
              [copy.buffer]
            );
          }
          return true;
        }
      }
      registerProcessor('recorder-worklet', RecorderWorklet);
    `;
  }

  // ──────────────────────────────────────────────────────
  // Fallback ScriptProcessor
  // ──────────────────────────────────────────────────────
  setupScriptProcessor() {
    this.processor = this.audioContext.createScriptProcessor(4096, 1, 1);
    this.processor.onaudioprocess = ev => {
      if (this.isRecording) {
        this.processAudioData(ev.inputBuffer.getChannelData(0));
      }
    };
    this.source.connect(this.processor);
    this.processor.connect(this.audioContext.destination); // required for onaudioprocess
  }

  // ──────────────────────────────────────────────────────
  // Core pipeline: resample → buffer → chunk → normalize → send
  // ──────────────────────────────────────────────────────
  processAudioData(input) {
    if (!input || input.length === 0) return;

    // ---- Resample (linear interpolation) ----
    const resampled = this.highQualityResample(input, this.resampleRatio);
    if (!resampled || resampled.length === 0) return;

    // ---- Append to pending buffer ----
    const newPending = new Float32Array(this.pendingChunkSamples.length + resampled.length);
    newPending.set(this.pendingChunkSamples);
    newPending.set(resampled, this.pendingChunkSamples.length);
    this.pendingChunkSamples = newPending;

    // ---- Emit full chunks ----
    while (this.pendingChunkSamples.length >= this.chunkSize) {
      const chunk = this.pendingChunkSamples.subarray(0, this.chunkSize);
      this.sendAudioChunk(new Float32Array(chunk));
      this.pendingChunkSamples = this.pendingChunkSamples.subarray(this.chunkSize);
    }
  }

  // ──────────────────────────────────────────────────────
  // Resampler (linear interpolation – fast & high quality)
  // ──────────────────────────────────────────────────────
  highQualityResample(input, ratio) {
    if (Math.abs(ratio - 1) < 1e-6) return new Float32Array(input); // copy

    const outLen = Math.floor(input.length / ratio);
    if (outLen <= 0) return new Float32Array(0);

    const out = new Float32Array(outLen);
    for (let i = 0; i < outLen; i++) {
      const src = i * ratio;
      const idx = Math.floor(src);
      const frac = src - idx;
      if (idx + 1 < input.length) {
        out[i] = input[idx] * (1 - frac) + input[idx + 1] * frac;
      } else {
        out[i] = input[idx];
      }
    }
    return out;
  }

  // ──────────────────────────────────────────────────────
  // Normalization helpers
  // ──────────────────────────────────────────────────────
  computePeak(arr) {
    let p = 0;
    for (let i = 0; i < arr.length; i++) {
      const a = Math.abs(arr[i]);
      if (a > p) p = a;
    }
    return p;
  }

  computeRms(arr) {
    let sum = 0;
    for (let i = 0; i < arr.length; i++) sum += arr[i] * arr[i];
    return arr.length ? Math.sqrt(sum / arr.length) : 0;
  }

  normalizeChunk(chunk) {
    // 1. Peak limiting
    const peak = this.computePeak(chunk);
    let scaled = chunk;
    if (peak > this.peakLimit) {
      const scale = this.peakLimit / peak;
      scaled = new Float32Array(chunk.length);
      for (let i = 0; i < chunk.length; i++) scaled[i] = chunk[i] * scale;
    } else {
      scaled = new Float32Array(chunk); // copy
    }

    // 2. RMS gain (bounded)
    const rms = this.computeRms(scaled);
    if (rms > 1e-9) {
      const target = Math.pow(10, this.normalizeToRmsDb / 20);
      let gain = target / rms;
      const maxGain = Math.pow(10, this.maxGainDb / 20);
      if (gain > maxGain) gain = maxGain;
      if (gain > 1.0005 || gain < 0.9995) {
        for (let i = 0; i < scaled.length; i++) scaled[i] *= gain;
      }
    }

    // 3. Hard clamp
    for (let i = 0; i < scaled.length; i++) {
      if (scaled[i] > 1) scaled[i] = 1;
      else if (scaled[i] < -1) scaled[i] = -1;
    }
    return scaled;
  }

  // ──────────────────────────────────────────────────────
  // Convert Float32 → little-endian ArrayBuffer
  // ──────────────────────────────────────────────────────
  float32ArrayToLEBuffer(float32) {
    const buf = new ArrayBuffer(float32.length * 4);
    const view = new DataView(buf);
    for (let i = 0; i < float32.length; i++) {
      view.setFloat32(i * 4, float32[i], true);
    }
    return buf;
  }

  // ──────────────────────────────────────────────────────
  // Send config frame (once)
  // ──────────────────────────────────────────────────────
  sendConfig() {
    if (!this.webSocketManager || this.sentConfig) return;
    const cfg = {
      type: 'config',
      encoding: 'float32',
      transport: 'websocket',
      sample_rate: this.targetSampleRate,
      channels: 1,
      bitDepth: 32,
    };
    try {
      this.webSocketManager.send(JSON.stringify(cfg));
      this.sentConfig = true;
    } catch (e) {
      console.warn('Failed to send config', e);
    }
  }

  // ──────────────────────────────────────────────────────
  // Volume monitoring (for UI mic-level indicator)
  // ──────────────────────────────────────────────────────
  setupVolumeAnalysis() {
    if (!this.audioContext || !this.audioStream) return;

    this.analyser = this.audioContext.createAnalyser();
    this.analyser.fftSize = 512;
    this.analyser.smoothingTimeConstant = 0.85;

    const src = this.audioContext.createMediaStreamSource(this.audioStream);
    src.connect(this.analyser);

    this.volumeDataArray = new Uint8Array(this.analyser.frequencyBinCount);
    this.isMonitoringVolume = true;
    this.monitorVolume();
  }

  monitorVolume() {
    if (!this.isMonitoringVolume || !this.analyser) return;

    this.analyser.getByteFrequencyData(this.volumeDataArray);
    let sum = 0;
    for (let i = 0; i < this.volumeDataArray.length; i++) sum += this.volumeDataArray[i];
    const avg = sum / this.volumeDataArray.length;

    // Emit only non-silence
    this.emit('volume', avg > 2 ? avg : 0);

    if (this.isMonitoringVolume) {
      requestAnimationFrame(() => this.monitorVolume());
    }
  }

  // ──────────────────────────────────────────────────────
  // Final send (binary Float32)
  // ──────────────────────────────────────────────────────
  sendAudioChunk(floatChunk) {
    if (!floatChunk || floatChunk.length === 0) return;

    const normalized = this.normalizeChunk(floatChunk);
    const buffer = this.float32ArrayToLEBuffer(normalized);

    try {
      if (this.webSocketManager && typeof this.webSocketManager.send === 'function') {
        this.webSocketManager.send(buffer);
      } else {
        this.emit('binaryAudio', buffer);
      }
    } catch (e) {
      console.error('sendAudioChunk error', e);
      this.emit('error', e.message || String(e));
    }
  }
}